// Shapes for box-level pieces: convex prisms (box, cylinder, hexagon, octagon, triangle, diamond) and
// ramps, fitted inside the piece's bounding box. Used for collision (brush planes) and drawing.
// Coordinates are local to the piece's centre, Source axes (X forward, Y left, Z up).

export type Shape = 'box' | 'cylinder' | 'hex' | 'oct' | 'tri' | 'diamond' | 'ramp' | 'wedge';

export const SHAPES: { id: Shape; label: string }[] = [
  { id: 'box', label: 'Box' },
  { id: 'cylinder', label: 'Round' },
  { id: 'hex', label: 'Hexagon' },
  { id: 'oct', label: 'Octagon' },
  { id: 'tri', label: 'Triangle' },
  { id: 'diamond', label: 'Diamond' },
  { id: 'ramp', label: 'Ramp' },
  { id: 'wedge', label: 'Surf ramp' },
];

/** Surf ramp (wedge): its ridge runs along X for even turns, along Y for odd ones. */
export const wedgeAlongX = (turn: number) => (((turn % 4) + 4) % 4) % 2 === 0;

/** Outline in the XY plane, counter-clockwise, inside [-hx, hx] x [-hy, hy]. `turn` = quarter turns. */
export function outline(shape: Shape, hx: number, hy: number, turn = 0): [number, number][] {
  let unit: [number, number][];
  const ring = (n: number, start: number) =>
    Array.from({ length: n }, (_, i) => {
      const a = start + (i / n) * Math.PI * 2;
      return [Math.cos(a), Math.sin(a)] as [number, number];
    });
  switch (shape) {
    case 'cylinder':
      unit = ring(20, 0);
      break;
    case 'hex':
      unit = ring(6, 0);
      break;
    case 'oct':
      unit = ring(8, Math.PI / 8);
      break;
    case 'tri':
      unit = [
        [-1, -1],
        [1, -1],
        [0, 1],
      ];
      break;
    case 'diamond':
      unit = [
        [1, 0],
        [0, 1],
        [-1, 0],
        [0, -1],
      ];
      break;
    default:
      unit = [
        [-1, -1],
        [1, -1],
        [1, 1],
        [-1, 1],
      ];
  }
  // Quarter turns in the unit square (so triangles can point any way), then fit the box.
  for (let t = 0; t < ((turn % 4) + 4) % 4; t++) unit = unit.map(([x, y]) => [-y, x]);
  return unit.map(([x, y]) => [x * hx, y * hy]);
}

/** Ramp: rises toward +X (after `turn` quarter turns) from the bottom to the top. */
export function rampRise(turn: number): [number, number] {
  const t = ((turn % 4) + 4) % 4;
  return [
    [1, 0],
    [0, 1],
    [-1, 0],
    [0, -1],
  ][t] as [number, number];
}

/**
 * Brush planes (nx, ny, nz, dist) of a piece of size (sx, sy, sz) centred on the origin, with
 * axis-aligned bevels so player boxes slide smoothly along it.
 */
export function shapePlanes(shape: Shape, sx: number, sy: number, sz: number, turn = 0): number[] {
  const hx = sx / 2;
  const hy = sy / 2;
  const hz = sz / 2;
  const planes: number[] = [];
  const push = (nx: number, ny: number, nz: number, d: number) => planes.push(nx, ny, nz, d);
  if (shape === 'wedge') {
    // Two slopes from a ridge along the length down to the long bottom edges, the bottom, the ends.
    const alongX = wedgeAlongX(turn);
    const half = alongX ? hy : hx;
    const len = Math.hypot(2 * hz, half);
    const k = (2 * hz) / len;
    const nz = half / len;
    const d = (hz * half) / len;
    if (alongX) {
      push(0, k, nz, d);
      push(0, -k, nz, d);
    } else {
      push(k, 0, nz, d);
      push(-k, 0, nz, d);
    }
    push(0, 0, -1, hz);
  } else if (shape === 'ramp') {
    // Box sides, bottom, and a sloped top from (-rise) at the bottom to (+rise) at the top.
    const [rx, ry] = rampRise(turn);
    const run = rx !== 0 ? sx : sy;
    const len = Math.hypot(run, sz);
    // Top plane through the high edge (z=+hz at +rise half) and the low edge (z=-hz at -rise half).
    const nz = run / len;
    const nh = -sz / len;
    push(nh * rx, nh * ry, nz, 0);
    push(0, 0, -1, hz);
  } else {
    const pts = outline(shape, hx, hy, turn);
    for (let i = 0; i < pts.length; i++) {
      const [ax, ay] = pts[i];
      const [bx, by] = pts[(i + 1) % pts.length];
      // Outward normal of a CCW edge: (dy, -dx).
      let nx = by - ay;
      let ny = -(bx - ax);
      const l = Math.hypot(nx, ny);
      if (l < 1e-9) continue;
      nx /= l;
      ny /= l;
      push(nx, ny, 0, nx * ax + ny * ay);
    }
    push(0, 0, 1, hz);
    push(0, 0, -1, hz);
  }
  // Axial bevels (the bounding box).
  push(1, 0, 0, hx);
  push(-1, 0, 0, hx);
  push(0, 1, 0, hy);
  push(0, -1, 0, hy);
  if (shape === 'ramp' || shape === 'wedge') push(0, 0, 1, hz);
  return planes;
}

/** Height of the walkable top at local (x, y), for placing things on pieces. */
export function topAt(
  shape: Shape,
  sz: number,
  x: number,
  y: number,
  sx: number,
  sy: number,
  turn = 0,
): number {
  if (shape === 'wedge') {
    const a = wedgeAlongX(turn) ? Math.abs(y) / (sy / 2) : Math.abs(x) / (sx / 2);
    return sz / 2 - Math.min(1, a) * sz;
  }
  if (shape !== 'ramp') return sz / 2;
  const [rx, ry] = rampRise(turn);
  const u = rx !== 0 ? (x * rx) / sx : (y * ry) / sy;
  return -sz / 2 + (u + 0.5) * sz;
}
